import { AttributeDataTypeEnum } from "@logex/expression-validator";

/** GLOBAL CONFIGURATION (DATASET/PROJECT) */
export interface FormConfiguration {
    author: string;
    datasets: FormConfigurationDataset[];
    settings?: FormConfigurationSettings;
    resources?: FormConfigurationImageDefinition[];
    name: string;
    label?: string;
    version?: string;
    tasks?: FormConfigurationTaskDefinition[];
}

export interface FormConfigurationTaskDefinition {
    task_id: string;
    labels: FormConfigurationLocalizedText[];
}

export interface FormConfigurationSectionImageDefinition {
    stateVariable: string;
    options: Record<string, FormConfigurationImageDefinition>;
}

export interface FormConfigurationLocalizedText {
    content?: string;
    language?: string;
}

export interface FormConfigurationImageDefinition {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
    disabled?: boolean;
    tooltip?: FormConfigurationLocalizedText[];
    title?: FormConfigurationLocalizedText[];
    titleColor?: string;
    /** Second color for split-color title rendering (bilateral/merged representatives). */
    titleColor2?: string;
    dotColor?: string;
}

export interface FormConfigurationSettings {
    defaultLanguage?: string;
    listPage: {
        columns: FormConfigurationListPageColumn[];
        defaultSortBy: string;
        defaultSortOrder: "ascending" | "descending";
        filterVariables?: string[];
    };
}

export interface FormConfigurationListPageColumn {
    name: string;
    label?: FormConfigurationLocalizedText[];
    dataset?: string;
    hasOther?: boolean;
    type?: AttributeDataTypeEnum;
    length?: number;
}

/** ENTITY CONFIGURATION (ENTITY/DATASET) */

export interface AddButtonVisibility {
    /** Whether the add button is shown inside the dataset control in a section. */
    section: boolean;
    /** Whether the add button is shown in the navigation menu. */
    navigation: boolean;
}

export interface FormConfigurationDataset {
    name: string;
    label: FormConfigurationLocalizedText[];
    addSubjectLabel?: FormConfigurationLocalizedText[];
    initializationSection: FormConfigurationSection;
    sections: FormConfigurationSection[];
    settings?: FormConfigurationDatasetSettings;
    addButtonVisibility?: AddButtonVisibility;
}

export interface FormConfigurationDatasetSettings {
    subjectLabelFormat?: SubjectLabelPart[];
    copySettings?: FormConfigurationCopySettings;
    sectionImage?: FormConfigurationSectionImageDefinition;
    mergedSubjects?: FormConfigurationMergedSubjectsView;
    subjectSort?: FormConfigurationSubjectSort[];
}

export interface FormConfigurationSubjectSort {
    sortBy: string;
    sortType: "string" | "number" | "date";
    sortOrder: "ascending" | "descending";
}

/**
 * Renders a bounded set of sibling subjects (same dataset, same parent) as parallel
 * columns instead of separate sidebar nodes. Models the "one logical subject with N
 * repeated attribute-sets" case where the data is physically split into one subject per
 * subgroup (e.g. a surgery whose left/right breast records are stored separately).
 *
 * A single `discriminatorVariable` identifies which subgroup each subject represents. Its
 * optionset drives the columns: every option becomes a column. Generic and opt-in per
 * dataset. When fewer subjects exist than subgroups, only the present columns render.
 */
export interface FormConfigurationMergedSubjectsView {
    /**
     * The single variable that keys which subgroup-column a subject belongs to, supplies the
     * (optional) column headers, and is hidden from the merged form body. Every merged subject
     * carries a unique value of this variable.
     */
    discriminatorVariable: string;
    /**
     * Explicit subgroup (column) order/labels. When omitted, subgroups are derived from the
     * discriminator optionset (value + label), so the view is fully driven by that single
     * variable with no duplicated config.
     */
    subgroups?: FormConfigurationSubjectSubgroup[];
    /** When true, suppress manual add/remove; subject count is pinned to the cardinality signal. */
    lockToCardinality?: boolean;
    /**
     * Label shown on the collapsed representative nav node (e.g. "Bilateral") instead of the
     * individual side's auto-derived label. When omitted the standard subject label is used.
     */
    mergedLabel?: FormConfigurationLocalizedText[];
    /**
     * Key in `sectionImage.options` that represents the merged/bilateral state (e.g. "bilateral").
     * When set, this option is used for the section header image and the nav tooltip image
     * whenever the representative is showing all subgroups.
     */
    mergedOptionKey?: string;
    /**
     * Variable names that must be skipped by the column-to-column "copy left/copy right" action.
     * These fields keep their own per-column value and are never overwritten from the source
     * column (e.g. device serial/lot/catalogue numbers that are specific to each side).
     */
    fieldsToIgnoreOnCopy?: string[];
}

/** One subgroup column of a merged-subjects view: a discriminator value and its header label. */
export interface FormConfigurationSubjectSubgroup {
    /** Discriminator value this subgroup represents. */
    value: string;
    label: FormConfigurationLocalizedText[];
}

export interface SubjectLabelPart {
    type: SubjectLabelPartType;
    value: string;
}

export enum SubjectLabelPartType {
    Variable = "variable",
    Text = "constant"
}

/** SECTION CONFIGURATION (SECTION + CATEGORY) */

export interface FormConfigurationSection {
    name: string;
    label: FormConfigurationLocalizedText[];
    categories: FormConfigurationSectionCategory[];
    datasets?: string[];
    settings?: FormConfigurationSectionSettings;
}

export interface FormConfigurationSectionSettings {
    addSubjectButton?: boolean;
    copySubjectButton?: boolean;
}

export interface FormConfigurationSectionCategory {
    name: string;
    label?: FormConfigurationLocalizedText[];
    elements: FormConfigurationElement[];
}

/** SECTION ELEMENT CONFIGURATION (CONTROLS ETC.) */

export type FormConfigurationElement =
    | FormConfigurationElementParagraph
    | FormConfigurationElementSetter
    | FormConfigurationElementCopySubject
    | FormConfigurationElementDatasetManager
    | FormConfigurationElementSimpleControl
    | FormConfigurationElementOptionControl;

export type FormConfigurationElementTypeMap = {
    [FormConfigurationElementType.Control]: FormConfigurationElementControlBase;
    [FormConfigurationElementType.Dataset]: FormConfigurationElementDatasetManager;
    [FormConfigurationElementType.CopySubject]: FormConfigurationElementCopySubject;
    [FormConfigurationElementType.Setter]: FormConfigurationElementSetter;
    [FormConfigurationElementType.Paragraph]: FormConfigurationElementParagraph;
};

export type FormConfigurationElementTypeMapper<
    T extends FormConfigurationElementType | undefined = undefined,
    V extends FormConfigurationElementControlType | undefined = undefined
> = T extends FormConfigurationElementType.Control
    ? V extends FormConfigurationElementControlType
        ? FormConfigurationElementControlTypeMap[V]
        : FormConfigurationElementControlBase
    : T extends FormConfigurationElementType
      ? FormConfigurationElementTypeMap[T]
      : FormConfigurationElementBase;

export enum FormConfigurationElementType {
    Paragraph = "paragraph",
    Setter = "setter",
    CopySubject = "copySubject",
    Dataset = "dataset",
    Control = "control"
}

export interface FormConfigurationElementBase {
    name: string;
    visibility?: FormConfigurationElementVisibility;
    label?: FormConfigurationLocalizedText[];
    help?: FormConfigurationLocalizedText[];
    type: FormConfigurationElementType;
    iconResource?: string;
}

export enum FormConfigurationElementVisibility {
    Readonly = "readonly",
    Hidden = "hidden"
}

/** PARAGRAPH ELEMENT*/

export interface FormConfigurationElementParagraph extends FormConfigurationElementBase {
    type: FormConfigurationElementType.Paragraph;
    prefillOptionsDef?: FormConfigurationOptionsDefinitionDynamic;
}

/** SETTER ELEMENT */

export interface FormConfigurationElementSetter extends FormConfigurationElementBase {
    type: FormConfigurationElementType.Setter;
    attributesToSet: string[];
    options: FormConfigurationExtensionSetterOption[];
}

export interface FormConfigurationExtensionSetterOption {
    value: string;
    label: FormConfigurationLocalizedText[];
}

/** DATASET MANAGER ELEMENT */
export interface FormConfigurationElementDatasetManager extends FormConfigurationElementBase {
    type: FormConfigurationElementType.Dataset;
    datasetName: string;
    /**
     * State-driven label overrides for the add button. Evaluated against the parent subject's
     * merged states + variables; the first matching entry provides the displayed label and an
     * optional createCount. When absent the default "Add <dataset>" label is used.
     */
    addButtonLabelOverrides?: FormConfigurationDatasetAddActionLabelOverride[];
    /**
     * Default number of times to open the create-subject dialog in sequence on a single button
     * click. Each dialog auto-submits when all fields are calculation-filled. Defaults to 1.
     * A matching `addButtonLabelOverrides` entry can override this per-state.
     */
    addButtonCreateCount?: number;
}

/**
 * A state-driven label override entry for the dataset add button.
 * The first entry (in order) whose conditions are all satisfied wins.
 */
export interface FormConfigurationDatasetAddActionLabelOverride {
    label: FormConfigurationLocalizedText[];
    conditions: FormConfigurationDatasetAddActionCondition[];
    /** Defaults to "all" — every condition must be true for the override to apply. */
    conditionMode?: "any" | "all";
    /** When set and this override matches, use this createCount instead of addButtonCreateCount. */
    createCount?: number;
}

/** A condition on a parent-subject state or variable value. */
export interface FormConfigurationDatasetAddActionCondition {
    field: string;
    value: string;
    operation?: "equals" | "notEquals";
}

/** COPY SUBJECT ELEMENT */
export interface FormConfigurationElementCopySubject extends FormConfigurationElementBase {
    type: FormConfigurationElementType.CopySubject;
    datasetName: string;
    copySettings?: FormConfigurationCopySettings;
}

export interface FormConfigurationCopySettings {
    fieldsToCopy?: string[];
    conditionalFieldsToCopy?: FormConfigurationConditionalFieldToCopy[];
    subjectToCopyFrom?: FormConfigurationSubjectToCopyFromType;
}

export interface FormConfigurationConditionalFieldToCopy {
    fieldToCopy: string;
    condition: FormConfigurationConditionalFieldToCopyCondition[];
}

export interface FormConfigurationConditionalFieldToCopyCondition {
    field: string;
    value: string;
    operation?: "equals" | "notEquals";
}

export enum FormConfigurationSubjectToCopyFromType {
    LatestDate = "latestDate"
}

/** CONTROL ELEMENT */

export type FormConfigurationElementControlType =
    | FormConfigurationElementSimpleControlType
    | FormConfigurationElementOptionControlType;

export type DataEntryUIControlType =
    | FormConfigurationElementSimpleControlType
    | FormConfigurationElementOptionControlType;
//| FormConfigurationElementLegacyControlType;

export type FormConfigurationElementControlTypeMap = {
    [key in FormConfigurationElementControlType]: key extends FormConfigurationElementSimpleControlType
        ? FormConfigurationElementSimpleControl
        : FormConfigurationElementOptionControl;
};

export const enum FormConfigurationElementOptionControlType {
    Radio = "radiobutton",
    Checkbox = "checkbox",
    Select = "dropdown",
    Autocomplete = "autocomplete",
    ImageRadio = "image-radio"
}

export const enum FormConfigurationElementSimpleControlType {
    Textarea = "textarea",
    Text = "text",
    Date = "calendar",
    DateTime = "datetime",
    Integer = "integer",
    Float = "float"
}

/**
const enum FormConfigurationElementLegacyControlType {
    Dataset = "dataset",
    Paragraph = "paragraph",
    ExternalDatasetCount = "external-dataset-count",
    ExternalDataset = "external-dataset",
    CreateButton = "create-button"
}
**/
export interface FormConfigurationElementControlBase extends FormConfigurationElementBase {
    type: FormConfigurationElementType.Control;
    attribute?: string;
    inputControl?: FormConfigurationElementControlType;
    editable?: boolean;
    format?: string;
    prefillOptionsDef?: FormConfigurationOptionsDefinition;
    barcodeScannerInput?: boolean;
}

/** SIMPLE CONTROL ELEMENT */

export interface FormConfigurationElementSimpleControl extends FormConfigurationElementControlBase {
    inputControl: FormConfigurationElementSimpleControlType;
}

/** OPTION CONTROL ELEMENT */

export interface FormConfigurationElementOptionControl extends FormConfigurationElementControlBase {
    inputControl: FormConfigurationElementOptionControlType;
    optionsDef: FormConfigurationOptionsDefinition;
    optionsDirection?: FormConfigurationOptionsDirection;
}

export const enum FormConfigurationOptionsDirection {
    Vertical = "vertical",
    Horizontal = "horizontal"
}

export type FormConfigurationOptionsDefinition =
    | FormConfigurationOptionsDefinitionStatic
    | FormConfigurationOptionsDefinitionDynamic;

export const enum FormConfigurationOptionsType {
    StaticOptionSet = "static-option-set",
    DynamicOptionSet = "dynamic-option-set"
}

/** STATIC OPTION SET */

export interface FormConfigurationOptionsDefinitionStatic {
    type: FormConfigurationOptionsType.StaticOptionSet;
    options: FormConfigurationOptionDefinition[];
}

export interface FormConfigurationOptionDefinition {
    value: string;
    label: FormConfigurationLocalizedText[];
    description?: FormConfigurationLocalizedText[];
    iconResource?: string;
    image?: FormConfigurationImageDefinition;
}

/** DYNAMIC OPTION SET */
export interface FormConfigurationOptionsDefinitionDynamic {
    type: FormConfigurationOptionsType.DynamicOptionSet;
    settings?: FormConfigurationOptionsDefinitionDynamicSettings;
    optionDefinition: FormConfigurationComplexOptionDefinition[];
    optionsSource: FormConfigurationDatasetOptionsSource;
}

export interface FormConfigurationOptionsDefinitionDynamicSettings {
    dialogTitle?: FormConfigurationLocalizedText[];
    dialogPickButtonLabel?: FormConfigurationLocalizedText[];
}

export interface FormConfigurationComplexOptionDefinition {
    name: string;
    showInList: boolean;
    targetFieldRefName: string;
    sourceAttribute: string;
}

export const enum FormConfigurationOptionsSourceType {
    Dataset = "dataset"
}
export interface FormConfigurationDatasetOptionsSource {
    type: FormConfigurationOptionsSourceType.Dataset;
    sourceDataset: {
        organisation?: string;
        project?: string;
        catalog?: boolean;
        name: string;
        relativePath?: string;
        search?: {
            field: string;
            placeholder: string;
        };
        keys?: {
            name: string;
            relativePath?: string;
        }[];
    };
    sortBy?: {
        attribute: string;
        order: "asc" | "desc";
    }[];
    filterBy?: {
        sourceAttributeName: string;
        operator: string;
        value?: string;
        targetAttributeName?: string;
    }[];
    pageSize?: number;
}
